<template>
  <div class="comment__main-block">
    <div class="comment__main-block-header-content">
      <BaseSubHeading
        size="medium"
        weight="500"
        color="markingRed-02"
        darkColor="markingRed-03"
        class="comment__main-block-header-content-status"
      >
        Deleted
      </BaseSubHeading>

      <BaseSubHeading
        size="medium"
        weight="400"
        class="comment__main-block-header-content-description"
      >
        {{ beforeTaskData.name }}
      </BaseSubHeading>

      <BaseSubHeading
        size="medium"
        weight="500"
        class="comment__main-block-header-content-type"
      >
        {{ formattedTaskType }}
      </BaseSubHeading>

      <BaseSubHeading
        weight="500"
        size="small"
        class="comment__main-block-header-content-time"
      >
        {{ beforeTaskData.estimatedHours }} hr
      </BaseSubHeading>
      <span class="comment__main-block-header-content-task-owner">
        <Avatar
          v-tooltip.bottom="{
            content: beforeTaskData.owner?.username,
            theme: 'info-tooltip-sm'
          }"
          :src="beforeTask?.owner?.pictureUrl ?? null"
          :gap="beforeTask?.owner?.getInitials()"
          size="md"
          type="avatarGroup"
          class="avatar-img"
        />
      </span>
    </div>
    <div
      class="comment__main-block-content"
      v-if="beforeTaskData.structuredDescription?.getTextFromHtml"
    >
      <span class="comment__main-block-content-text">
        {{ beforeTaskData.structuredDescription.getTextFromHtml }}
      </span>
    </div>
    <div class="comment__main-block-footer">
      <button
        class="link is-medium comment__main-block-footer-deleted-link"
        @click="showInfo"
      >
        View deleted Task
      </button>
      <BaseSubHeading
        v-if="canRestoreTask"
        class="separator"
        color="accent-01"
        darkColor="accent-200"
      >
        |
      </BaseSubHeading>
      <button
        v-if="canRestoreTask"
        class="link is-medium comment__main-block-footer-restore-link"
        @click="restoreTask"
      >
        Restore
      </button>
    </div>
    <Teleport to="body">
      <ModalDeleteTask
        v-if="comment"
        :comment="comment"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from "vue"
import { useStore } from "vuex"

import Avatar from "@/components/Avatar/index.vue"
import ModalDeleteTask from "@/components/Modal/ModalDeleteTask.vue"

import { Comment, Task, TaskTypeOption } from "@/models"
import { $t } from "@/plugins/i18n"
import { Actions } from "@/store/actions"
import { Getters } from "@/store/getters"

import type { PropType } from "vue"

const props = defineProps({
  comment: {
    type: Object as PropType<Comment>,
    required: true
  }
})

const store = useStore()
const { proxy } = getCurrentInstance() || {}

const beforeTask = computed(() => {
  const taskBefore = props.comment.itemAuditRecord?.taskBefore
  return taskBefore ? Task.create(taskBefore) : null
})

const beforeTaskData = computed(() => {
  return (
    beforeTask.value || {
      name: "",
      type: "",
      estimatedHours: 0,
      owner: { username: "", pictureUrl: null },
      structuredDescription: { getTextFromHtml: "" }
    }
  )
})

const formattedTaskType = computed(() => {
  const type = beforeTaskData.value?.type
  if (!type) return ""
  return $t(`cycleTimeByTaskType.${type}`)
})

const isTaskRestored = computed(() => {
  return props.comment.itemAuditRecord?.taskBefore?.isTaskRestored ?? false
})

const availableTaskTypes = computed<TaskTypeOption[]>(
  () => store.getters[Getters.AVAILABLE_TASK_TYPES]
)

const canRestoreTask = computed(() => {
  return (
    isTaskRestored.value &&
    availableTaskTypes.value.some(type => type.id === beforeTask.value?.type)
  )
})

const restoreTask = async () => {
  await store.dispatch(Actions.RESTORE_TASK, {
    itemId: props.comment.targetResourceId?.id,
    taskData: props.comment.itemAuditRecord?.taskBefore?.restoreTaskUrl,
    taskName: props.comment.itemAuditRecord?.taskBefore?.name,
    commentId: props.comment.id
  })
  store.dispatch(Actions.UPDATE_COMMENT_RESTORE, {
    id: props.comment.id
  })
}

const showInfo = () => {
  if (proxy && proxy.$modal) {
    proxy.$modal.show(`item-task-deleted-${props.comment.id}`)
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_ellipsis";
@import "@/assets/scss/utils/mixins/_flex";

.comment__main-block-header-content-task-owner {
  > div:first-child {
    background: var(--accent-03);
  }
}

.comment__main-block {
  @include flex($direction: column);

  gap: var(--step2);
}

.comment__main-block-header-content {
  display: grid;
  align-items: center;
  gap: var(--step2);
  grid-template-columns: 64px 1fr max-content 78px 58px;

  &-description {
    @include ellipsis;
  }

  &-type {
    width: max-content;
    min-width: 40px;
    margin-left: var(--step2);
    text-align: center;
  }

  &-time {
    @include flex($direction: row, $align: center, $justify: center);

    height: 18px;
    border-radius: 2px;
    margin-left: var(--step2);
    background: var(--monochrome-01);
    gap: 2px;
    line-height: 1;
  }

  &-task-owner {
    margin-left: var(--step2);

    @include flex($justify: center, $align: center);
  }
}

.comment__main-block-content {
  display: grid;
  max-width: 100%;
  grid-template-columns: 1fr auto;

  @include ellipsis;

  &-text {
    @include ellipsis;
  }
}

.comment__main-block-footer {
  @include flex($align: baseline);

  margin-top: var(--step2);
  gap: var(--step3);

  &-deleted-link {
    font-weight: 500;
  }

  &-restore-link {
    font-weight: 500;
  }
}

.separator {
  display: flex;
  height: 100%;
  align-items: center;
  font-size: 18px;
  line-height: 1;
}
</style>
